'use client'

/**
 * SHARED STATE FOR THE GUEST SIGN-UP NUDGES.
 *
 * WHY THIS EXISTS. Two independent nudges reach an engaged logged-out visitor - GuestEngagementNudge
 * (the browse-path card on ticker pages, after 3 distinct symbols) and SiteAccessGate's Nudge (the
 * generic card on any open page, after 6 pageviews). They used SEPARATE sessionStorage keys with no
 * shared suppression, so an engaged guest could be shown BOTH cards in one session (register:
 * push/guest audit 2026-09-22). Two asks at once reads as nagging, and nagging costs the very
 * conversion the cards exist to win. This module is the ONE place that answers "has this guest already
 * been asked this session", so the two surfaces cannot double-fire.
 *
 * IT ALSO REMEMBERS ACROSS SESSIONS. Every prior counter was sessionStorage, so a guest who comes back
 * a second or third time - arguably the HIGHEST intent of all - looked identical to a first-timer and
 * got the same cold, generic ask (or none). localStorage here survives the session boundary, so a
 * return visitor can be met sooner and with copy that names what they already did.
 *
 * ⚠ Everything is best-effort and wrapped: private mode / disabled storage must degrade to "not
 * claimed, no memory", never throw on a page-load path.
 */

/** One-nudge-per-session claim, shared by BOTH surfaces. sessionStorage: resets each new session. */
const CLAIM_KEY = 'dt_guest_nudge_shown'

/** Cross-session memory of the guest's engagement. localStorage: survives the session boundary. */
const MEMORY_KEY = 'dt_guest_v1'
/** Marks THIS session as counted, so a session is tallied once, not once per page view. */
const SESSION_MARK = 'dt_guest_session_mark'

/** Distinct symbols we bother to remember across sessions, capped so the record cannot grow forever. */
const MAX_REMEMBERED_SYMBOLS = 40

export type GuestMemory = {
  /** Distinct browsing sessions we have seen this guest in (>= 1 once noteGuestSession has run). */
  sessions: number
  /** Distinct tickers viewed across all sessions, newest last, capped at MAX_REMEMBERED_SYMBOLS. */
  symbols: string[]
  /** ms epoch of first and most recent sighting. */
  firstSeen: number
  lastSeen: number
}

const EMPTY: GuestMemory = { sessions: 0, symbols: [], firstSeen: 0, lastSeen: 0 }

function readMemory(): GuestMemory {
  try {
    const raw = localStorage.getItem(MEMORY_KEY)
    if (!raw) return { ...EMPTY }
    const m = JSON.parse(raw) as Partial<GuestMemory>
    return {
      sessions: typeof m.sessions === 'number' ? m.sessions : 0,
      symbols: Array.isArray(m.symbols) ? m.symbols.filter((s): s is string => typeof s === 'string') : [],
      firstSeen: typeof m.firstSeen === 'number' ? m.firstSeen : 0,
      lastSeen: typeof m.lastSeen === 'number' ? m.lastSeen : 0,
    }
  } catch {
    return { ...EMPTY }
  }
}

function writeMemory(m: GuestMemory): void {
  try { localStorage.setItem(MEMORY_KEY, JSON.stringify(m)) } catch { /* private mode: no memory */ }
}

/** Read the guest's cross-session engagement without mutating anything. */
export function getGuestMemory(): GuestMemory {
  return readMemory()
}

/**
 * Count this browsing session once. Uses a sessionStorage MARK as the session boundary (a plain
 * localStorage tally would count every page navigation, so one visit would look like twenty). Returns
 * the updated memory. Safe to call on every page load; only the first call per session mutates.
 */
export function noteGuestSession(): GuestMemory {
  const m = readMemory()
  let firstThisSession = false
  try {
    if (!sessionStorage.getItem(SESSION_MARK)) {
      sessionStorage.setItem(SESSION_MARK, '1')
      firstThisSession = true
    }
  } catch {
    // No sessionStorage: cannot delimit a session, so do not inflate the count. Treat as read-only.
    return m
  }
  if (!firstThisSession) return m
  const now = Date.now()
  const next: GuestMemory = {
    sessions: m.sessions + 1,
    symbols: m.symbols,
    firstSeen: m.firstSeen || now,
    lastSeen: now,
  }
  writeMemory(next)
  return next
}

/** Record a distinct ticker the guest viewed (across sessions). Returns the updated distinct count. */
export function recordGuestSymbol(symbol: string): number {
  const sym = symbol.toUpperCase()
  const m = readMemory()
  if (!m.symbols.includes(sym)) {
    m.symbols = [...m.symbols, sym].slice(-MAX_REMEMBERED_SYMBOLS)
    m.lastSeen = Date.now()
    writeMemory(m)
  }
  return m.symbols.length
}

/** True once this guest is on a second-or-later session: a returning visitor. */
export function isReturningGuest(): boolean {
  return readMemory().sessions >= 2
}

/** Has EITHER guest nudge already been shown this session? The shared suppression check. */
export function guestNudgeClaimed(): boolean {
  try { return sessionStorage.getItem(CLAIM_KEY) === '1' } catch { return false }
}

/** Claim the one-nudge-per-session slot. Call the moment a surface decides to show its card. */
export function claimGuestNudge(): void {
  try { sessionStorage.setItem(CLAIM_KEY, '1') } catch { /* private mode: cannot suppress, accept it */ }
}
